import { LoaderFunctionArgs, useLoaderData } from "react-router"
import { DocsUiLink as Link } from "~/docs-data/docs-link"
import { MapPinIcon, SplitIcon, VectorSquareIcon } from "lucide-react"
import type { LucideIcon } from "lucide-react"
import { H1, H2, P, Section } from "~/components/Typography"
import { Card, CardHeader, CardTitle, CardDescription } from "~/components/ui/card"
import { MarkdownBody } from "~/components/MarkdownBody"
import { CustomBoundariesIcon } from "~/components/CustomBoundariesIcon"
import { canonicalMetricSlugForType, metricCanonicalName } from "~/docs-data/helpers"
import { locationSets } from "~/docs-data/concepts"
import { getDocsCatalogForRequest } from "~/server/docs-catalog-store.server"
import type { DatasetEntry } from "~/docs-data/datasets"

const ICON_BY_TYPE: Record<string, LucideIcon> = {
  place: MapPinIcon,
  area: VectorSquareIcon,
  way: SplitIcon,
}

// A single card on the location-type page represents a "metric concept" —
// every cadence × map-level × prefix variant whose display name collapses to
// the same canonical name. For example, on Area the cards
//   - `foot_traffic_day` + `foot_traffic_month` + `cbg_foot_traffic_month` …
//     → one "Foot Traffic" card
//   - `trade_areas_day` + `trade_areas_day_al2` + `cbg_trade_area` …
//     → one "Trade Areas" card
// We compute the union of cadences and map shortNames across all member
// datasets so the card description can advertise the variety without listing
// every binding.
type FamilyCard = {
  conceptId: string
  variantCount: number
  // Canonical display name shared by every member.
  name: string
  // Pre-stripped shortDescription from the first member, used as a fallback.
  shortDescription: string
  cadences: string[]
  mapNames: string[]
  // Link target — the first member LTM's page.
  linkLtm: string
}

const CADENCE_ORDER: Record<string, number> = {
  Hourly: 0,
  Daily: 1,
  Weekly: 2,
  Monthly: 3,
  Quarterly: 4,
  Yearly: 5,
}

const buildFamilyCards = (
  datasets: DatasetEntry[],
  locationTypeId: string,
): FamilyCard[] => {
  const byConcept = new Map<string, DatasetEntry[]>()
  const conceptOrder: string[] = []
  for (const d of datasets) {
    if (d.locationType !== locationTypeId) continue
    const concept = metricCanonicalName(d.name)
    if (!byConcept.has(concept)) {
      byConcept.set(concept, [])
      conceptOrder.push(concept)
    }
    byConcept.get(concept)!.push(d)
  }
  conceptOrder.sort((a, b) => a.localeCompare(b))
  return conceptOrder.map((concept) => {
    const members = byConcept.get(concept)!
    const first = members[0]
    const variantLTMs = new Set(members.map((d) => d.ltm))
    const cadences = Array.from(new Set(members.map((d) => d.cadence))).sort(
      (a, b) => (CADENCE_ORDER[a] ?? 99) - (CADENCE_ORDER[b] ?? 99),
    )
    const mapNames = Array.from(
      new Set(
        members.map(
          (d) =>
            d.locationSetName ??
            locationSets.find((ls) => ls.id === d.locationSet)?.shortName ??
            d.locationSet,
        ),
      ),
    )
    return {
      conceptId: concept,
      variantCount: variantLTMs.size,
      name: concept,
      shortDescription: first.shortDescription,
      cadences,
      mapNames,
      // Single-variant concept → skip the overview and link to that LTM
      // directly. Multi-variant → typed canonical-overview slug scoped to
      // this location type.
      linkLtm:
        variantLTMs.size === 1
          ? first.ltm
          : canonicalMetricSlugForType(concept, locationTypeId),
    }
  })
}

export const loader = async ({ request, params }: LoaderFunctionArgs) => {
  const id = params.id ?? ""
  const { locationTypes, datasets } = await getDocsCatalogForRequest(request)

  const lt = locationTypes.find((x) => x.id === id)
  if (!lt) throw new Response("Location type not found", { status: 404 })

  const familyCards = buildFamilyCards(datasets, id)

  return { lt, familyCards }
}

export default function LocationTypePage() {
  const { lt, familyCards } = useLoaderData<typeof loader>()
  const Icon = ICON_BY_TYPE[lt.id]

  return (
    <div className="max-w-screen-xl">
      <Section>
        <div className="flex items-center gap-3 mb-2">
          {Icon && (
            <span className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
              <Icon className="w-5 h-5 text-foreground" />
            </span>
          )}
          <H1 className="!mb-0">{lt.displayName}</H1>
        </div>
        {lt.description ? (
          <MarkdownBody source={lt.description} />
        ) : (
          <P className="text-muted-foreground italic">No description yet.</P>
        )}
      </Section>

      {(lt.id === "place" || lt.id === "area") && (
        <Section>
          <div id="custom-boundaries" className="rounded-lg border border-dashed bg-card p-5 flex items-start gap-4">
            <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
              <CustomBoundariesIcon className="h-5 w-5" />
            </span>
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-1">
                Custom boundaries
              </h3>
              <P className="text-sm text-muted-foreground !mb-0">
                You are not limited to the pre-built location sets. If you have
                your own map (trade zones, sales territories, property
                outlines, catchment polygons), Pine59 can attribute its metrics
                to boundaries you define, so the data lines up with the
                geography your organization already thinks in. Reach out to{" "}
                <a
                  href="mailto:support@pine59.com"
                  className="text-primary hover:underline"
                >
                  support@pine59.com
                </a>{" "}
                to set up custom boundaries for your account.
              </P>
            </div>
          </div>
        </Section>
      )}

      <Section>
        <H2>Available metrics</H2>
        {familyCards.length === 0 ? (
          <P className="text-muted-foreground italic">
            No metrics with materialized datasets for this location type yet.
          </P>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {familyCards.map((f) => (
              <Card key={f.conceptId}>
                <CardHeader>
                  <CardTitle className="text-base">
                    <Link
                      to={`/docs/concepts/metrics/${f.linkLtm}`}
                      variant="inline"
                    >
                      {f.name}
                    </Link>
                  </CardTitle>
                  {f.shortDescription && (
                    <CardDescription>{f.shortDescription}</CardDescription>
                  )}
                  {f.variantCount > 1 && (
                    <div className="mt-2 text-xs text-muted-foreground space-y-1">
                      <div>
                        <span className="font-medium text-foreground">Cadences:</span>{" "}
                        {f.cadences.join(", ")}
                      </div>
                      <div>
                        <span className="font-medium text-foreground">Maps:</span>{" "}
                        {f.mapNames.join(", ")}
                      </div>
                    </div>
                  )}
                </CardHeader>
              </Card>
            ))}
          </div>
        )}
      </Section>
    </div>
  )
}
